import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function VerifyEmail() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const [status, setStatus] = useState("loading"); // loading | success | error
  const [message, setMessage] = useState("");
  const navigate = useNavigate();
  const token = params.get("token");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage(t("verify.missingToken"));
      return;
    }
    api.get(`/auth/verify-email`, { params: { token } })
      .then(() => setStatus("success"))
      .catch((e) => {
        setStatus("error");
        const d = e?.response?.data?.detail;
        setMessage(typeof d === "string" ? d : t("verify.genericError"));
      });
  }, [token, t]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAF9] p-6">
      <div className="max-w-md w-full bg-white border border-[#EBEBEA] rounded-2xl p-10 text-center">
        {status === "loading" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto text-[#1C7C54] animate-spin mb-4" />
            <h1 className="font-display text-2xl font-semibold text-[#0A110E]">{t("verify.verifying")}</h1>
          </>
        )}
        {status === "success" && (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-[#1C7C54]/10 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-7 h-7 text-[#1C7C54]" />
            </div>
            <h1 className="font-display text-2xl font-semibold text-[#0A110E] mb-2">{t("verify.success")}</h1>
            <p className="text-sm text-[#5F7368] mb-6">{t("verify.successDesc")}</p>
            <button
              data-testid="verify-continue-btn"
              onClick={() => navigate("/")}
              className="w-full px-6 py-3 rounded-xl bg-[#0F291E] text-white font-semibold hover:bg-[#1C7C54] transition-colors"
            >
              {t("verify.continue")}
            </button>
          </>
        )}
        {status === "error" && (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-[#D95D39]/10 flex items-center justify-center mb-4">
              <XCircle className="w-7 h-7 text-[#D95D39]" />
            </div>
            <h1 className="font-display text-2xl font-semibold text-[#0A110E] mb-2">{t("verify.failed")}</h1>
            <p className="text-sm text-[#5F7368] mb-6">{message}</p>
            <Link to="/" data-testid="verify-back-link" className="text-[#1C7C54] hover:underline">{t("verify.backLink")}</Link>
          </>
        )}
      </div>
    </div>
  );
}
